<template>
  <div class="cap-page" data-testid="capability-map-shell">
    <div class="cap-header">
      <div>
        <h1 class="cap-title">Capability Map</h1>
        <p class="cap-subtitle">
          Your product's capabilities, synced from Feature1, mapped to how this site communicates
          and campaigns for them. Feature1 stays the source of truth — this view never edits capabilities.
        </p>
      </div>
      <div class="cap-header-actions">
        <button
          v-if="isAdmin"
          type="button"
          class="cap-btn-secondary"
          data-testid="cap-collect-button"
          :disabled="store.collecting"
          @click="store.collectResults(siteId)"
        >{{ store.collecting ? 'Collecting…' : 'Collect results' }}</button>
        <button
          v-if="isAdmin"
          type="button"
          class="cap-btn-secondary"
          data-testid="cap-scan-button"
          :disabled="store.scanning"
          @click="store.scanMentions(siteId)"
        >{{ store.scanning ? 'Scanning…' : 'Scan mentions' }}</button>
        <button
          v-if="isAdmin"
          type="button"
          class="cap-btn-primary"
          data-testid="cap-sync-button"
          :disabled="store.syncing"
          @click="store.syncNow(siteId)"
        >{{ store.syncing ? 'Syncing…' : 'Sync from Feature1' }}</button>
      </div>
    </div>

    <p v-if="store.syncError" class="cap-sync-error" data-testid="cap-sync-error">{{ store.syncError }}</p>
    <p v-else-if="store.lastSync" class="cap-sync-note" data-testid="cap-sync-note">
      Last sync: {{ store.lastSync.inserted }} new, {{ store.lastSync.updated }} updated.
    </p>
    <p v-if="store.scanError" class="cap-sync-error" data-testid="cap-scan-error">{{ store.scanError }}</p>
    <p v-else-if="store.scanRun?.status === 'success'" class="cap-sync-note" data-testid="cap-scan-note">
      Mention scan: {{ store.scanRun.stats?.links_created ?? 0 }} new links across
      {{ store.scanRun.stats?.mentions_found ?? 0 }} site mentions and
      {{ store.scanRun.stats?.post_matches_found ?? 0 }} post matches.
    </p>
    <p v-else-if="store.scanning" class="cap-sync-note" data-testid="cap-scan-progress">
      Scanning the site and social posts for capability mentions — this can take a few minutes…
    </p>
    <p v-if="store.collectError" class="cap-sync-error" data-testid="cap-collect-error">{{ store.collectError }}</p>
    <p v-else-if="store.collectRun?.status === 'success'" class="cap-sync-note" data-testid="cap-collect-note">
      Results collection: {{ store.collectRun.stats?.metrics_collected ?? 0 }} publications measured,
      {{ (store.collectRun.stats?.result_links_created ?? 0) + (store.collectRun.stats?.result_links_updated ?? 0) }}
      capability results refreshed.
    </p>

    <!-- Exactly one data state renders, driven by the store's discriminator. -->
    <div v-if="store.uiState === 'loading'" class="cap-state" data-testid="cap-state-loading">
      Loading capability map…
    </div>

    <div v-else-if="store.uiState === 'error'" class="cap-state cap-state-error" data-testid="cap-state-error">
      <p>{{ store.error }}</p>
      <button type="button" class="cap-btn-secondary" @click="store.loadCoverage(siteId)">Retry</button>
    </div>

    <div v-else-if="store.uiState === 'empty'" class="cap-state" data-testid="cap-state-empty">
      <p>No capabilities synced yet.</p>
      <p v-if="isAdmin" class="cap-state-hint">Run "Sync from Feature1" to pull your capability portfolio.</p>
      <p v-else class="cap-state-hint">Ask a workspace admin to run the Feature1 sync.</p>
    </div>

    <div v-else data-testid="cap-state-ready">
      <!-- Coverage summary -->
      <div class="cap-summary" data-testid="cap-summary">
        <span class="cap-chip" data-coverage="covered">{{ store.summary.covered }} covered</span>
        <span class="cap-chip" data-coverage="partial">{{ store.summary.partial }} partial</span>
        <span class="cap-chip" data-coverage="uncovered">{{ store.summary.uncovered }} uncovered</span>
        <span class="cap-chip-total">{{ store.summary.total }} capabilities</span>
      </div>

      <!-- Persona → module → capability map -->
      <section v-for="group in store.grouped" :key="group.persona" class="cap-persona">
        <h2 class="cap-persona-title">{{ group.persona }}</h2>
        <div v-for="mod in group.modules" :key="mod.module" class="cap-module">
          <h3 class="cap-module-title">{{ mod.module }}</h3>
          <div class="cap-grid">
            <article
              v-for="cap in mod.capabilities"
              :key="cap.id"
              class="cap-card"
              :data-coverage="cap.coverage"
              data-testid="cap-card"
            >
              <header class="cap-card-head">
                <span class="cap-card-name">{{ cap.capability_name }}</span>
                <span class="cap-badge" :data-coverage="cap.coverage">{{ COVERAGE_LABELS[cap.coverage] }}</span>
              </header>
              <p v-if="cap.description" class="cap-card-desc">{{ cap.description }}</p>
              <p v-if="cap.lifecycle_state === 'archived'" class="cap-card-archived">Archived in Feature1</p>
              <dl class="cap-card-counts">
                <div><dt>Mentions</dt><dd>{{ cap.mention_count }}</dd></div>
                <div><dt>Posts</dt><dd>{{ cap.post_count }}</dd></div>
                <div><dt>Campaigns</dt><dd>{{ cap.campaign_count }}</dd></div>
                <div><dt>Results</dt><dd>{{ cap.result_count }}</dd></div>
              </dl>
              <div v-if="cap.routes.length" class="cap-card-routes">
                <span v-for="route in cap.routes" :key="route" class="cap-route-chip">{{ route }}</span>
              </div>
            </article>
          </div>
        </div>
      </section>
    </div>

    <span class="cap-sr-only" aria-live="polite">{{ store.announce }}</span>
  </div>
</template>

<script setup>
// Capability Map (Feature1 capability growth integration).
//
// Site-scoped read view over the synced capability projection: coverage of
// website communication (mentions per route) and media activity (posts,
// campaigns, results) per capability. Sync is admin-only and additive —
// nothing here writes back to Feature1.

import { computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useCapabilitiesStore } from '../stores/capabilities'
import { useSitesStore } from '../stores/sites'

const props = defineProps({ id: { type: String, required: true } })

const route = useRoute()
const store = useCapabilitiesStore()
const sitesStore = useSitesStore()

const COVERAGE_LABELS = { covered: 'Covered', partial: 'Partial', uncovered: 'Uncovered' }

const siteId = computed(() => props.id || route.params.id).value
const isAdmin = computed(() => Boolean(sitesStore.user?.isAdmin || sitesStore.user?.is_admin))

onMounted(() => store.loadCoverage(siteId))
watch(() => route.params.id, (next, prev) => {
  if (next && next !== prev) store.loadCoverage(next)
})
</script>

<style scoped>
.cap-page { max-width: 1100px; margin: 0 auto; padding: 24px 16px 48px; }
.cap-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.cap-title { font-size: 1.5rem; font-weight: 700; color: var(--text-primary); margin: 0 0 4px; }
.cap-subtitle { color: var(--text-secondary); font-size: 0.9rem; margin: 0; max-width: 640px; }
.cap-header-actions { flex-shrink: 0; display: flex; gap: 8px; }

.cap-btn-primary {
  background: var(--accent); color: #fff; border: none; border-radius: var(--radius-sm);
  padding: 9px 16px; font-size: 0.875rem; font-weight: 600; cursor: pointer;
  box-shadow: 0 0 18px var(--accent-glow); transition: all 0.15s;
}
.cap-btn-primary:hover { background: var(--accent-light); }
.cap-btn-primary:disabled { opacity: 0.6; cursor: default; box-shadow: none; }
.cap-btn-secondary {
  background: var(--glass-bg); color: var(--text-primary); border: var(--glass-border); border-radius: var(--radius-sm);
  padding: 7px 14px; font-size: 0.875rem; cursor: pointer; transition: background 0.15s;
}
.cap-btn-secondary:hover { background: var(--glass-bg-hover); }
.cap-sync-error { color: var(--danger); font-size: 0.85rem; margin: 10px 0 0; }
.cap-sync-note { color: var(--text-secondary); font-size: 0.85rem; margin: 10px 0 0; }

.cap-state { text-align: center; color: var(--text-secondary); padding: 64px 16px; }
.cap-state-error p { color: var(--danger); margin-bottom: 12px; }
.cap-state-hint { font-size: 0.85rem; margin-top: 6px; }

.cap-summary { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin: 20px 0 8px; }
.cap-chip {
  font-size: 0.8rem; font-weight: 600; border-radius: 999px; padding: 3px 10px;
  background: var(--glass-bg-active); color: var(--text-secondary);
}
.cap-chip[data-coverage='covered'] { background: rgba(52,211,153,0.18); color: var(--success); }
.cap-chip[data-coverage='partial'] { background: rgba(251,191,36,0.18); color: var(--warning); }
.cap-chip[data-coverage='uncovered'] { background: rgba(248,113,113,0.18); color: var(--danger); }
.cap-chip-total { font-size: 0.8rem; color: var(--text-secondary); margin-left: 4px; }

.cap-persona { margin-top: 28px; }
.cap-persona-title { font-size: 1.05rem; font-weight: 700; color: var(--text-primary); margin: 0 0 4px; }
.cap-module { margin: 12px 0 0 4px; }
.cap-module-title { font-size: 0.85rem; font-weight: 600; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.04em; margin: 0 0 8px; }

.cap-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; }
.cap-card {
  background: var(--glass-bg); border: var(--glass-border); border-radius: var(--radius-md); padding: 14px;
  border-left-width: 3px; color: var(--text-primary);
  backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur); box-shadow: var(--elev-sm);
}
.cap-card[data-coverage='covered'] { border-left-color: var(--success); }
.cap-card[data-coverage='partial'] { border-left-color: var(--warning); }
.cap-card[data-coverage='uncovered'] { border-left-color: var(--danger); }
.cap-card-head { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
.cap-card-name { font-weight: 600; color: var(--text-primary); font-size: 0.95rem; }
.cap-badge { font-size: 0.7rem; font-weight: 700; border-radius: 999px; padding: 2px 8px; white-space: nowrap; }
.cap-badge[data-coverage='covered'] { background: rgba(52,211,153,0.18); color: var(--success); }
.cap-badge[data-coverage='partial'] { background: rgba(251,191,36,0.18); color: var(--warning); }
.cap-badge[data-coverage='uncovered'] { background: rgba(248,113,113,0.18); color: var(--danger); }
.cap-card-desc { color: var(--text-secondary); font-size: 0.82rem; margin: 6px 0 0; }
.cap-card-archived { color: var(--text-muted); font-size: 0.75rem; font-style: italic; margin: 6px 0 0; }

.cap-card-counts { display: flex; gap: 14px; margin: 10px 0 0; }
.cap-card-counts div { display: flex; flex-direction: column; }
.cap-card-counts dt { font-size: 0.68rem; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.03em; }
.cap-card-counts dd { font-size: 0.95rem; font-weight: 700; color: var(--text-primary); margin: 0; }

.cap-card-routes { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.cap-route-chip {
  font-size: 0.72rem; font-family: ui-monospace, monospace; background: var(--glass-bg-active);
  color: var(--text-secondary); border-radius: 6px; padding: 2px 7px;
}

.cap-sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}
</style>
